import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import api from '@/lib/api'

// ─── Types ───
export interface NdAttachment {
  id: string
  file_path: string
  original_name: string
  file_size: number
  mime_type: string | null
}

export interface NdReply {
  id: string
  user_id: string
  user_name: string
  is_admin: boolean
  message: string
  created_at: string
  attachments?: NdAttachment[]
}

export interface NdTicket {
  id: string
  ticket_number: number
  subject: string
  description: string
  type: string
  subtype: string | null
  module: string | null
  priority: string
  tags: string[]
  status: string
  sdp_ticket_id: number | null
  user_id: string
  user_name?: string | null
  user_email?: string | null
  assigned_to?: string | null
  assigned_to_name?: string | null
  assigned_to_email?: string | null
  created_at: string
  updated_at: string
  attachments: NdAttachment[]
  replies: NdReply[]
}

export interface NdInternalUser {
  id: string
  email: string
  first_name: string
  last_name: string
  full_name: string
}

export interface NdImprovement {
  ticket_id: string
  ticket_number: number
  subject: string
  module: string | null
  user_name: string | null
  melhoria_status: string
  estimated_hours: string | number | null
  estimated_cost: string | number | null
  roi_forecast: string | null
  roi_period: string | null
  planned_publish_date: string | null
  actual_publish_date: string | null
  include_in_roadmap: boolean
  rejection_reason: string | null
  analysis_notes: string | null
  created_at: string
}

export interface NdCatalogs {
  types: string[]
  subtypes: string[]
  modules: string[]
  tags: string[]
}

export interface NdSettings {
  admin_email: string | null
  support_email: string | null
  ticket_types: string[]
  ticket_subtypes: string[]
  ticket_modules: string[]
  ticket_tags: string[]
  recipient_matrix: Record<string, Record<string, string>>
  sdp_enabled: boolean
  sdp_base_url: string | null
  sdp_status_map: Record<string, string>
  sdp_last_seen_request_id: number | null
  sdp_has_credentials: boolean
  portal_logo_url: string | null
  portal_bg_image_url: string | null
  portal_primary_color: string | null
  portal_dark_mode_default: boolean
}

export interface NdFaqItem {
  id: string
  question: string
  answer: string
  display_order: number
  is_active: boolean
}

export interface NdFaqCategory {
  id: string
  name: string
  slug: string
  display_order: number
  items: NdFaqItem[]
}

export interface NdEmailTemplateVariation {
  id: string
  module: string | null
  subtype: string | null
  subject: string
  body: string
}

export interface NdEmailTemplate {
  id: string
  base_key: string
  title: string
  recipient_tag: string
  category: string
  subject: string
  body: string
  supports_variations: boolean
  placeholders: string[]
  variations: NdEmailTemplateVariation[]
}

const KEY = 'nai-desk'

// ─── Catalogs (used in form dropdowns) ───
export function useNdCatalogs() {
  return useQuery({
    queryKey: [KEY, 'catalogs'],
    queryFn: async () => (await api.get<NdCatalogs>('/v1/portal/admin/catalogs')).data,
  })
}

// ─── Tickets (cliente) ───
export function useMyTickets(filters: {
  q?: string; status?: string; type?: string; subtype?: string; module?: string;
  page?: number; per_page?: number
} = {}) {
  return useQuery({
    queryKey: [KEY, 'my-tickets', filters],
    queryFn: async () => (await api.get<NdTicket[]>('/v1/portal/tickets', { params: filters })).data,
  })
}

export function useTicket(ticketId: string | undefined) {
  return useQuery({
    queryKey: [KEY, 'ticket', ticketId],
    queryFn: async () => (await api.get<NdTicket>(`/v1/portal/tickets/${ticketId}`)).data,
    enabled: !!ticketId,
  })
}

export function useCreateTicket() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (form: FormData) => {
      const { data } = await api.post<NdTicket>('/v1/portal/tickets', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      return data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: [KEY] }),
  })
}

export function useReplyTicket(ticketId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (input: string | { message: string; files?: File[] }) => {
      // Compat: aceita string (sem anexos) ou { message, files }
      const message = typeof input === 'string' ? input : input.message
      const files = typeof input === 'string' ? [] : (input.files || [])
      const fd = new FormData()
      fd.append('message', message)
      files.forEach(f => fd.append('files', f))
      const { data } = await api.post<NdReply>(`/v1/portal/tickets/${ticketId}/reply`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      return data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: [KEY] }),
  })
}

// ─── Tickets (admin) ───
export function useAdminTickets(filters: {
  q?: string; status?: string; type?: string; module?: string;
  page?: number; per_page?: number
} = {}) {
  return useQuery({
    queryKey: [KEY, 'admin-tickets', filters],
    queryFn: async () => (await api.get<NdTicket[]>('/v1/portal/tickets/admin/list', { params: filters })).data,
  })
}

export function useInternalUsers() {
  return useQuery({
    queryKey: [KEY, 'internal-users'],
    queryFn: async () => (await api.get<NdInternalUser[]>('/v1/portal/tickets/admin/internal-users')).data,
  })
}

export function useForwardTicket() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ ticketId, assignedTo, message }:
        { ticketId: string; assignedTo: string; message?: string }) => {
      const { data } = await api.put(`/v1/portal/tickets/admin/${ticketId}/forward`,
        { assigned_to: assignedTo, message })
      return data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: [KEY] }),
  })
}

export function useUpdateTicketStatus() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ ticketId, status }: { ticketId: string; status: string }) => {
      const { data } = await api.put(`/v1/portal/tickets/admin/${ticketId}/status`, { status })
      return data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: [KEY] }),
  })
}

// ─── Melhorias ───
export function useMyImprovements() {
  return useQuery({
    queryKey: [KEY, 'my-melhorias'],
    queryFn: async () => (await api.get<NdImprovement[]>('/v1/portal/melhorias')).data,
  })
}

export function useImprovementsRoadmap() {
  return useQuery({
    queryKey: [KEY, 'roadmap'],
    queryFn: async () => (await api.get<NdImprovement[]>('/v1/portal/melhorias/roadmap')).data,
  })
}

export function useAdminImprovements(filters: { module?: string; melhoria_status?: string } = {}) {
  return useQuery({
    queryKey: [KEY, 'admin-melhorias', filters],
    queryFn: async () => (await api.get<NdImprovement[]>('/v1/portal/melhorias/admin/list', { params: filters })).data,
  })
}

export function useUpdateMelhoriaAnalysis() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ ticketId, payload }: { ticketId: string; payload: Partial<NdImprovement> }) => {
      const { data } = await api.put(`/v1/portal/melhorias/admin/${ticketId}`, payload)
      return data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: [KEY] }),
  })
}

export function useChangeMelhoriaStatus() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ ticketId, action }: { ticketId: string; action: 'approve'|'reject'|'development'|'publish' }) => {
      const { data } = await api.put(`/v1/portal/melhorias/admin/${ticketId}/status`, { action })
      return data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: [KEY] }),
  })
}

// ─── FAQ ───
export function useFaq() {
  return useQuery({
    queryKey: [KEY, 'faq'],
    queryFn: async () => (await api.get<NdFaqCategory[]>('/v1/portal/faq')).data,
  })
}

export function useFaqSearch(q: string) {
  return useQuery({
    queryKey: [KEY, 'faq-search', q],
    queryFn: async () => (await api.get<NdFaqItem[]>('/v1/portal/faq/search', { params: { q } })).data,
    enabled: q.length >= 2,
  })
}

export function useCreateFaqCategory() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (payload: { name: string; slug: string; display_order: number }) => {
      const { data } = await api.post('/v1/portal/faq/admin/categories', payload)
      return data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: [KEY, 'faq'] }),
  })
}

export function useDeleteFaqCategory() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => api.delete(`/v1/portal/faq/admin/categories/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: [KEY, 'faq'] }),
  })
}

export function useCreateFaqItem() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (payload: { category_id: string; question: string; answer: string; display_order: number; is_active: boolean }) => {
      const { data } = await api.post('/v1/portal/faq/admin/items', payload)
      return data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: [KEY, 'faq'] }),
  })
}

export function useUpdateFaqItem() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, payload }: { id: string; payload: any }) => {
      const { data } = await api.put(`/v1/portal/faq/admin/items/${id}`, payload)
      return data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: [KEY, 'faq'] }),
  })
}

export function useDeleteFaqItem() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => api.delete(`/v1/portal/faq/admin/items/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: [KEY, 'faq'] }),
  })
}

// ─── Settings (admin) ───
export function useSettings() {
  return useQuery({
    queryKey: [KEY, 'settings'],
    queryFn: async () => (await api.get<NdSettings>('/v1/portal/admin/settings')).data,
  })
}

export function useUpdateSettings() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (payload: Partial<NdSettings> & { sdp_client_id?: string; sdp_client_secret?: string; sdp_refresh_token?: string }) => {
      const { data } = await api.put('/v1/portal/admin/settings', payload)
      return data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: [KEY] }),
  })
}

export function useTestSdp() {
  return useMutation({
    mutationFn: async () => (await api.post('/v1/portal/admin/settings/sdp/test')).data,
  })
}

export function useSyncSdpSeq() {
  return useMutation({
    mutationFn: async () => (await api.post('/v1/portal/admin/settings/sdp/sync-seq')).data,
  })
}

// ─── Email Templates (admin) ───
export function useEmailTemplates() {
  return useQuery({
    queryKey: [KEY, 'email-templates'],
    queryFn: async () => (await api.get<NdEmailTemplate[]>('/v1/portal/admin/email-templates')).data,
  })
}

export function useUpdateEmailTemplate() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, payload }: { id: string; payload: { subject?: string; body?: string } }) => {
      const { data } = await api.put(`/v1/portal/admin/email-templates/${id}`, payload)
      return data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: [KEY, 'email-templates'] }),
  })
}

export function useCreateEmailVariation() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (payload: { template_id: string; module?: string|null; subtype?: string|null; subject: string; body: string }) => {
      const { data } = await api.post('/v1/portal/admin/email-templates/variations', payload)
      return data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: [KEY, 'email-templates'] }),
  })
}

export function useDeleteEmailVariation() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => api.delete(`/v1/portal/admin/email-templates/variations/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: [KEY, 'email-templates'] }),
  })
}
